import { WhereClausePreview } from '@modules/common/components/WhereClausePreview';
import { WhereClausePreviewProps } from '@modules/common/components/WhereClausePreview/WhereClausePreview';
import { isEmptyWhereClause } from '@modules/common/utils';
import {
  DEFAULT_COMPARE_FILTER_A_NAME,
  DEFAULT_COMPARE_FILTER_B_NAME,
} from '@modules/quantify/constants';
import { useGetCompareColors } from '@modules/quantify/hooks/useGetCompareColors';
import { useQuantifyStore } from '@modules/quantify/store/quantifyStore';
import { Box, Stack, Typography } from '@mui/material';
import { EQLFunctionName } from 'v2/core/repository/eql/types';
import { WhereClause } from 'v2/core/services/queryBuilder/whereClause';

interface MeasuredAsMetricDenominatorPreviewProps {
  isDisabled?: boolean;
  isReadOnly?: boolean;
  onClick?: () => void;
  WhereClausePreviewProps?: Partial<WhereClausePreviewProps>;
}

export function MeasuredAsMetricDenominatorPreview({
  isDisabled,
  isReadOnly,
  onClick,
  WhereClausePreviewProps,
}: MeasuredAsMetricDenominatorPreviewProps) {
  const { colorA, colorB } = useGetCompareColors();
  const compareTo = useQuantifyStore(({ query }) => query.compareTo);
  const denominatorWhereClause = useQuantifyStore(
    ({ query }) => query.denominatorWhereClause,
  );
  const isPercentageMetric = useQuantifyStore(
    ({ query }) => query.formulaID === EQLFunctionName.NormaliseByPercent,
  );
  const compareFilterAName = useQuantifyStore(
    ({ metadata }) =>
      metadata.compareQBField.compareQBFieldAName ||
      DEFAULT_COMPARE_FILTER_A_NAME,
  );
  const compareFilterBName = useQuantifyStore(
    ({ metadata }) =>
      metadata.compareQBField.compareQBFieldBName ||
      DEFAULT_COMPARE_FILTER_B_NAME,
  );

  if (!isPercentageMetric) {
    return null;
  }

  const renderWhereClausePreview = (whereClause: WhereClause | null) => {
    const isWhereClauseAbsent = !whereClause || isEmptyWhereClause(whereClause);

    return isWhereClauseAbsent ? (
      <Typography variant="body2" color="text.secondary">
        No filters applied
      </Typography>
    ) : (
      <Box
        onClick={onClick}
        sx={{
          pointerEvents: isReadOnly ? 'none' : 'initial',
          cursor: isDisabled
            ? 'not-allowed'
            : isReadOnly
            ? 'initial'
            : 'pointer',
          opacity: isDisabled ? 0.5 : 1,
          flex: 1,
        }}
      >
        <WhereClausePreview
          whereClause={whereClause}
          sxProps={{
            flex: 1,
            ...WhereClausePreviewProps?.sxProps,
          }}
          {...WhereClausePreviewProps}
        />
      </Box>
    );
  };

  if (compareTo?.type === 'whereClause') {
    const renderCompareToWhereClausePreview = (isFilterA: boolean) => {
      return (
        <Stack direction="column" spacing={0.5}>
          <Stack direction="row" spacing={0.5} alignItems="center">
            <Box
              sx={{
                backgroundColor: isFilterA ? colorA : colorB,
                borderRadius: '50%',
                p: 0.5,
                width: '4px',
              }}
            />
            <Typography variant="caption" color="text.disabled">
              {isFilterA ? compareFilterAName : compareFilterBName}
            </Typography>
          </Stack>
          {renderWhereClausePreview(
            isFilterA
              ? denominatorWhereClause
              : compareTo.denominatorWhereClause,
          )}
        </Stack>
      );
    };

    return (
      <Stack direction="column" spacing={1}>
        {renderCompareToWhereClausePreview(true)}
        {renderCompareToWhereClausePreview(false)}
      </Stack>
    );
  }

  return renderWhereClausePreview(denominatorWhereClause);
}
